import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/providers/user";
import { zodResolver } from "@hookform/resolvers/zod";
import * as LucideIcons from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { DataService } from "@/services/data";
import { Combobox } from "@/components/ui/combobox";
import { useCRM } from "@/providers/crm";

const deadNumberFormSchema = z.object({
    campaign: z.string().min(1, { message: "Campaign is required" }),
    confirmed_by_prospect: z.boolean().refine(val => val === true, {
        message: "You must confirm that this information was given to you by a prospect over the phone"
    }),
    prospect_name_or_comment: z.string().min(1, { message: "Prospect name or comment is required" })
});

type DeadNumberFormData = z.infer<typeof deadNumberFormSchema>;

type DeadNumberResponse = {
    success: boolean;
    results: any[];
    redirect_url?: string;
};

function DeadNumberModal({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const form = useForm<DeadNumberFormData>({
        resolver: zodResolver(deadNumberFormSchema),
        defaultValues: {
            campaign: "",
            confirmed_by_prospect: false,
            prospect_name_or_comment: ""
        }
    });

    const onSubmit = async (data: DeadNumberFormData) => {
        setIsSubmitting(true);
        try {
            const response = (await DataService.client.url("/crm/app/global-actions/report-dead-number/").post(data).json()) as DeadNumberResponse;

            if (response.success) {
                toast.success("Dead number reported successfully");
                if (response.redirect_url) {
                    // Handle redirect if needed
                    window.location.href = response.redirect_url;
                }
                setOpen(false);
                form.reset();
            } else {
                toast.error("Failed to report dead number");
            }
        } catch (error) {
            console.error("Error reporting dead number:", error);
            toast.error("An error occurred while reporting the dead number");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        form.reset();
    };

    // Check if form is valid for submit button
    const isFormValid = form.watch("confirmed_by_prospect") && form.watch("campaign").trim() !== "" && form.watch("prospect_name_or_comment").trim() !== "";

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Report a dead number</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="campaign"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-sm font-medium">
                                        Campaign <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input placeholder="Enter campaign name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="confirmed_by_prospect"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel className="text-sm font-normal">I confirm that this information was given to me by a prospect over the phone</FormLabel>
                                        <FormMessage />
                                    </div>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="prospect_name_or_comment"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-sm font-medium">
                                        Prospect name or comment <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Textarea placeholder="Enter prospect name or add a comment" className="resize-none" rows={3} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button type="submit" disabled={!isFormValid || isSubmitting}>
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}

const rebalanceApprenticeshipCasesFormSchema = z.object({
    confirmed: z.boolean().refine(val => val === true, {
        message: "You must confirm that you understand the impact of this rebalance"
    }),
    mode: z.enum(["preserve_scores", "equalize_scores"])
});

type RebalanceApprenticeshipCasesFormData = z.infer<typeof rebalanceApprenticeshipCasesFormSchema>;

type RebalanceApprenticeshipCasesResponse = {
    success: boolean;
    message?: string;
    error?: string;
};

function RebalanceApprenticeshipCasesModal({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const form = useForm<RebalanceApprenticeshipCasesFormData>({
        resolver: zodResolver(rebalanceApprenticeshipCasesFormSchema),
        defaultValues: {
            confirmed: false,
            mode: "preserve_scores"
        }
    });

    const onSubmit = async (data: RebalanceApprenticeshipCasesFormData) => {
        setIsSubmitting(true);
        try {
            const response = (await DataService.client
                .url("/crm/app/global-actions/rebalance-apprenticeship-cases/")
                .post(data)
                .json()) as RebalanceApprenticeshipCasesResponse;

            if (response.success) {
                toast.success(response.message || "Apprenticeship case rebalance started");
                setOpen(false);
                form.reset();
            } else {
                toast.error(response.error || "Failed to start apprenticeship case rebalance");
            }
        } catch (error) {
            console.error("Error starting apprenticeship case rebalance:", error);
            // wretch rejects non-2xx responses before `.json()` resolves. Surface the
            // backend message (e.g. the 409 "already running" conflict) when available.
            const wretchError = error as { status?: number; json?: RebalanceApprenticeshipCasesResponse };
            const apiMessage = wretchError?.json?.error;
            if (apiMessage) {
                toast.error(apiMessage);
            } else {
                toast.error("An error occurred while starting the rebalance");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        form.reset();
    };

    const isFormValid = form.watch("confirmed") === true;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <DialogTitle>Rebalance apprenticeship cases</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm text-muted-foreground">
                    <p>
                        This action redistributes active apprenticeship cases evenly across all active CSM users from
                        the CSM picklist, moving as few cases as possible: only cases from departed owners or exceeding
                        volume quotas are reassigned, to preserve pedagogical continuity.
                    </p>
                    <p>
                        Moves are processed asynchronously: you will receive a notification with the planned
                        distribution as soon as they are enqueued.
                    </p>
                </div>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="mode"
                            render={({ field }) => (
                                <FormItem className="space-y-2">
                                    <FormLabel>Score strategy</FormLabel>
                                    <FormControl>
                                        <RadioGroup value={field.value} onValueChange={field.onChange} className="space-y-1">
                                            <FormItem className="flex items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="preserve_scores" />
                                                </FormControl>
                                                <div className="space-y-0.5 leading-none">
                                                    <FormLabel className="text-sm font-normal">Preserve scores</FormLabel>
                                                    <p className="text-xs text-muted-foreground">
                                                        Each previously active CSM keeps an assiduity average as close as
                                                        possible to their current score.
                                                    </p>
                                                </div>
                                            </FormItem>
                                            <FormItem className="flex items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="equalize_scores" />
                                                </FormControl>
                                                <div className="space-y-0.5 leading-none">
                                                    <FormLabel className="text-sm font-normal">Equalize scores</FormLabel>
                                                    <p className="text-xs text-muted-foreground">
                                                        Reassigned cases are chosen so every CSM's assiduity average converges
                                                        toward the global average — without extra moves.
                                                    </p>
                                                </div>
                                            </FormItem>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="confirmed"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel className="text-sm font-normal">
                                            I understand that cases will be reassigned to balance CSM volumes following
                                            the selected score strategy
                                        </FormLabel>
                                        <FormMessage />
                                    </div>
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="submit" disabled={!isFormValid || isSubmitting}>
                                {isSubmitting ? "Starting..." : "Start rebalance"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}

const reallocateFormSchema = z.object({
    from_sf_user_id: z.string().min(1, { message: "Sales representative who left the company is required" }),
    campaign_id: z.string().min(1, { message: "Former campaign group is required" }),
    to_sf_user_id: z.string().min(1, { message: "Target sales representative is required" })
});

type ReallocateFormData = z.infer<typeof reallocateFormSchema>;

function ReallocateOutboundSalesRepsModal({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeOutboundAgents, setActiveOutboundAgents] = useState<Array<{ value: string; label: string }>>([]);
    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const form = useForm<ReallocateFormData>({
        resolver: zodResolver(reallocateFormSchema),
        defaultValues: {
            from_sf_user_id: "",
            campaign_id: "",
            to_sf_user_id: ""
        }
    });

    type ReallocateResponse = {
        success: boolean;
        results: any[];
        redirect_url?: string;
    };

    const onSubmit = async (data: ReallocateFormData) => {
        setIsSubmitting(true);
        try {
            const response = (await DataService.client.url("/crm/app/global-actions/reallocate-outbound-sales-reps/").post(data).json()) as ReallocateResponse;

            if (response.success) {
                toast.success("Reallocation request submitted successfully");
                if (response.redirect_url) {
                    window.location.href = response.redirect_url;
                }
                setOpen(false);
                form.reset();
            } else {
                toast.error("Failed to submit reallocation request");
            }
        } catch (error) {
            console.error("Error submitting reallocation request:", error);
            toast.error("An error occurred while submitting the request");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        form.reset();
    };

    // Load outbound agents when modal opens
    useEffect(() => {
        const loadAgents = async () => {
            setIsLoadingAgents(true);
            try {
                const active = (await DataService.client.url("/crm/app/picklists/outbound-agents/active/").get().json()) as Array<{ value: string; label: string }>;
                setActiveOutboundAgents(active ?? []);
            } catch (error) {
                console.error("Failed to load outbound agents", error);
                toast.error("Failed to load outbound agents");
            } finally {
                setIsLoadingAgents(false);
            }
        };
        if (open) {
            loadAgents();
        }
    }, [open]);

    const campaignGroups = [
        { value: "415", label: "Outbound DPC/IPERIA" },
        { value: "366", label: "Outbound CPF" }
    ];

    const isFormValid = form.watch("from_sf_user_id").trim() !== "" && form.watch("campaign_id").trim() !== "" && form.watch("to_sf_user_id").trim() !== "";

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Reallocate Outbound Sales Rep Qualified Callbacks</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="from_sf_user_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-sm font-medium">
                                        Sales Representative who left the company <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <Combobox
                                        name={field.name}
                                        options={activeOutboundAgents}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        placeholder={isLoadingAgents ? "Loading..." : "--None--"}
                                        className="w-full"
                                        disabled={isLoadingAgents}
                                    />
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="campaign_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-sm font-medium">
                                        Former campaign group <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="--None--" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {campaignGroups.map(group => (
                                                <SelectItem key={group.value} value={group.value}>
                                                    {group.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="to_sf_user_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-sm font-medium">
                                        Target Sales Representative <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <Combobox
                                        name={field.name}
                                        options={activeOutboundAgents}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        placeholder={isLoadingAgents ? "Loading..." : "--None--"}
                                        className="w-full"
                                        disabled={isLoadingAgents}
                                    />
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button type="submit" disabled={!isFormValid || isSubmitting}>
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}

export function GlobalActionsDropdown() {
    const [open, setOpen] = useState(false);
    const [deadNumberModalOpen, setDeadNumberModalOpen] = useState(false);
    const [reallocateOutboundSalesRepsModalOpen, setReallocateOutboundSalesRepsModalOpen] = useState(false);
    const [rebalanceApprenticeshipCasesModalOpen, setRebalanceApprenticeshipCasesModalOpen] = useState(false);
    const { clearCache } = useCRM();
    const { has_any_profile } = useUser();

    const handleOpenDeadNumberModal = () => {
        setOpen(false);
        setDeadNumberModalOpen(true);
    };

    const handleOpenReallocateOutboundSalesRepsModal = () => {
        setOpen(false);
        setReallocateOutboundSalesRepsModalOpen(true);
    };

    const handleOpenRebalanceApprenticeshipCasesModal = () => {
        setOpen(false);
        setRebalanceApprenticeshipCasesModalOpen(true);
    };

    const globalActions = [
        {
            name: "Report spammed phone number",
            icon: LucideIcons.PhoneCall,
            onClick: handleOpenDeadNumberModal
        },
        {
            name: "Reallocate outbound sales reps callbacks",
            icon: LucideIcons.Shuffle,
            onClick: handleOpenReallocateOutboundSalesRepsModal,
            allowed_profiles: ["system_admin" as const]
        },
        {
            name: "Rebalance apprenticeship cases",
            icon: LucideIcons.Scale,
            onClick: handleOpenRebalanceApprenticeshipCasesModal,
            allowed_profiles: ["system_admin" as const]
        },
        {
            name: "Clear metadata cache",
            icon: LucideIcons.RefreshCw,
            onClick: () => {
                clearCache();
                toast.success("Metadata cache cleared successfully");
            }
        }
    ];

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <div className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100">
                        <SimpleTooltip message="Global actions" delayDuration={300} skipDelayDuration={300}>
                            <div className="flex h-8 items-center justify-center rounded-full" onClick={() => setOpen(!open)}>
                                <LucideIcons.Zap size={18} strokeWidth={1.75} className="text-gray-700" />
                            </div>
                        </SimpleTooltip>
                    </div>
                </PopoverTrigger>

                <PopoverContent className="mr-10 w-72 p-0" align="start">
                    <div className="flex flex-col">
                        {/* Header avec compteur */}
                        <div className="flex items-center justify-between border-b px-4 py-3">
                            <h3 className="text-sm font-semibold">Global actions</h3>
                        </div>

                        {/* Liste des favoris */}
                        <ScrollArea className="h-fit w-full">
                            <div className="w-full p-2">
                                {globalActions
                                    .filter(action => {
                                        // Si pas de restriction de profil, afficher l'action
                                        if (!action.allowed_profiles) return true;
                                        // Sinon, vérifier si l'utilisateur a un des profils autorisés
                                        return has_any_profile(action.allowed_profiles);
                                    })
                                    .map(action => {
                                        return (
                                            <div key={action.name} className="group relative mb-1 w-full cursor-pointer rounded-md hover:bg-gray-100" onClick={action.onClick}>
                                                <div className="flex w-full items-center justify-between px-2 py-2">
                                                    <div className="flex max-w-64 items-center  gap-2 overflow-hidden truncate pr-2 text-sm font-medium">
                                                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-denim-blue">
                                                            <action.icon size={16} className="text-white" />
                                                        </div>
                                                        <div className="truncate">{action.name}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </ScrollArea>
                    </div>
                </PopoverContent>
            </Popover>
            <DeadNumberModal open={deadNumberModalOpen} setOpen={setDeadNumberModalOpen} />
            <ReallocateOutboundSalesRepsModal open={reallocateOutboundSalesRepsModalOpen} setOpen={setReallocateOutboundSalesRepsModalOpen} />
            <RebalanceApprenticeshipCasesModal
                open={rebalanceApprenticeshipCasesModalOpen}
                setOpen={setRebalanceApprenticeshipCasesModalOpen}
            />
        </>
    );
}
